import React, { RefObject, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import MeetingsFilterViewModel, { MAX_CHECKED_MEETINGS_COUNT } from './meetings-filter.viewmodel';
import { MeetingsReportTableLayout } from '../../meetings-report-table-layout/meetings-report-table-layout';
import { Paging } from '../../../../../global/_paging/paging-container';
import { IMeetingsReportItem, IReportFilterItem, IReportOperator } from '../../../../../../api/reports.dao';
import { ReportConfigurationViewModel } from '../../../_reports/report-configuration/report-configuration.viewmodel';
import './meetings-filter.scss';

interface IProps {
    intl: IntlShape;
    vm: MeetingsFilterViewModel;
    configurationVm: ReportConfigurationViewModel;
    field: string;
    ContentHeader: JSX.Element;
    headerRef: RefObject<HTMLDivElement>;
}

const MeetingsFilter = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        vm,
        configurationVm,
        field,
        ContentHeader,
        headerRef
    } = props;

    const [curPage, setCurPage] = useState<number>(1);

    const isEmpty = vm && !vm.meetingsList?.length && !vm.loading;

    const tableList: IMeetingsReportItem[] = vm && vm.meetingsList?.map(value => {
        let presentersOrgName: string[] = value.presenters.length ? value.presenters.map(organization => organization.name) : [];
        return {
            ...value,
            id: String(value.meetingId),
            presenters: presentersOrgName
        };
    });

    const { applyFilter, clearFilter, getAppliedFilter } = configurationVm;

    const appliedFilter = getAppliedFilter(field);


    const isValueSelected = (value: string): boolean => {
        return appliedFilter?.values.includes(value) || false;
    };

    const filterCommonProps: { field: string; customField: boolean; operator: IReportOperator } = {
        field: field,
        customField: false,
        operator: 'IN',
    };

    const selectAll = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
        if (checked) {
            const meetingsItems: IReportFilterItem[] = vm.meetingsList?.map(val => ({ value: String(val.meetingId) }));
            applyFilter({
                ...filterCommonProps, values: meetingsItems.map(el => el.value)
            });
        } else {
            clearFilter(field);
        }
    };

    const selectOne = (value: string) => {
        if (isValueSelected(value)) {
            const newValues = appliedFilter.values.filter(el => el !== value);

            if (newValues.length) {
                applyFilter({ ...filterCommonProps, values: appliedFilter.values.filter(el => el !== value) });
            } else {
                clearFilter(field);
            }
        } else {
            applyFilter({ ...filterCommonProps, values: [...(appliedFilter?.values || []), value].filter(el => el !== undefined) });
        }
    };


    const isValueDisabled = (value: string): boolean => {
        return !!(appliedFilter?.values.length >= MAX_CHECKED_MEETINGS_COUNT && !appliedFilter?.values.includes(value));
    };

    const checkedAll = !!appliedFilter?.values.length
        && appliedFilter?.values.length === vm.meetingsList?.length
        && curPage === vm.getPaginationVM().getCurrentPage()
        && (JSON.stringify(vm.meetingsList?.map(el => String(el.meetingId))) === JSON.stringify(appliedFilter?.values.map(el => el)));

    return (
        <div className='meetings-filter'>
            <div className='meetings-filter__header' ref={headerRef}>
                {ContentHeader}
                <i className='meetings-filter__operator'>Select the meetings for which you would like attendee interaction data.</i>
            </div>
            {
                vm && <>
                    <MeetingsReportTableLayout
                        isEmpty={isEmpty}
                        handleCheckAll={(e: React.ChangeEvent<HTMLInputElement>) => {
                            selectAll(e);
                            setCurPage(vm.getPaginationVM().getCurrentPage());
                        }}
                        list={tableList}
                        loading={vm.loading}
                        eventTimezone={vm.getEventTimezone()}
                        checkOne={selectOne}
                        isValueChecked={isValueSelected}
                        isValueDisabled={isValueDisabled}
                        isCheckAllDisabled={vm.loading || (appliedFilter?.values.length >= MAX_CHECKED_MEETINGS_COUNT && !checkedAll)}
                        checkedAll={checkedAll}
                    />
                    {!vm.loading && <Paging pagingVM={vm.getPaginationVM()} />}
                </>
            }
        </div>
    );
}));


export { MeetingsFilter };
